// Commercial pricing engine — Fase C (Iter46) frontend mirror.
// Kept 1-to-1 with /app/backend/commercial_engine.py so the UI can
// preview prices in real time without a round-trip. Backend is still the
// source of truth: rollup is re-computed on save.

// SanMar category → commercial group
export const CATEGORY_TO_GROUP = {
  "T-Shirts": "T_SHIRTS",
  Caps: "HATS",
  Activewear: "APPAREL",
  "Polos/Knits": "APPAREL",
  "Sweatshirts/Fleece": "APPAREL",
  Outerwear: "APPAREL",
  "Woven Shirts": "APPAREL",
  Workwear: "APPAREL",
  "Personal Protection": "APPAREL",
  Bottoms: "APPAREL",
  Bags: "APPAREL",
  Accessories: "APPAREL",
  "Infant & Toddler": "APPAREL",
  Youth: "APPAREL",
  "Women's": "APPAREL",
  Tall: "APPAREL",
};

export const VALID_GROUPS = ["T_SHIRTS", "APPAREL", "HATS"];

export function resolveCommercialGroup(category, categoriesAll) {
  const seen = [];
  if (category) seen.push(category);
  if (Array.isArray(categoriesAll)) {
    for (const c of categoriesAll) {
      if (c && !seen.includes(c)) seen.push(c);
    }
  }
  const hits = seen.map((c) => CATEGORY_TO_GROUP[c]).filter(Boolean);
  if (hits.includes("HATS")) return "HATS";
  if (hits.includes("T_SHIRTS")) return "T_SHIRTS";
  if (hits.includes("APPAREL")) return "APPAREL";
  return null;
}

export function isLongSleeve(productName, subCategory) {
  const txt = [productName, subCategory].filter(Boolean).join(" ").toLowerCase();
  return txt.includes("long sleeve") || txt.includes("long-sleeve");
}

// Returns [amount, label]. 2XL/3XL → +$3.00, 4XL+ → +$4.00. Else 0.
export function sizeUplift(size) {
  if (!size) return [0, ""];
  const s = String(size).trim().toUpperCase().replace(/\s/g, "");
  for (let n = 4; n < 10; n++) {
    if (s === `${n}XL` || s === "X".repeat(n) + "L") return [4.0, `${n}XL uplift`];
  }
  if (s === "2XL" || s === "XXL") return [3.0, "2XL uplift"];
  if (s === "3XL" || s === "XXXL") return [3.0, "3XL uplift"];
  return [0, ""];
}

// Tier tables (kept identical to Python)
export const T_SHIRTS_MIN_QTY = 6;
export const T_SHIRTS_TIERS = [
  [6, 11, 25.0, "6–11"],
  [12, 23, 19.99, "12–23"],
  [24, 47, 18.99, "24–47"],
  [48, 71, 17.99, "48–71"],
  [72, 143, 14.99, "72–143"],
  [144, null, 12.99, "144+"],
];

export const APPAREL_MIN_QTY = 6;
export const APPAREL_TIER_DISCOUNTS = [
  [6, 11, 0.0, "6–11"],
  [12, 23, 1.0, "12–23"],
  [24, 47, 2.0, "24–47"],
  [48, 71, 3.0, "48–71"],
  [72, 143, 4.0, "72–143"],
  [144, null, 5.0, "144+"],
];

// BEANIES (Jun 2026) — mínimo 6 combinado; [min, max, multiplicador, floor, label]
export const BEANIES_MIN_QTY = 6;
export const BEANIES_TIERS = [
  [6, 11, 3.0, 18.99, "6–11"],
  [12, 23, 2.8, 17.99, "12–23"],
  [24, 47, 2.3, 15.99, "24–47"],
  [48, 71, 2.0, 13.99, "48–71"],
  [72, 143, 1.8, 11.99, "72–143"],
  [144, null, 1.65, 9.99, "144+"],
];

// Beanie/knit-cap elegible por nombre (genérico, espejo de is_beanie Python).
export function isBeanie(productName) {
  const txt = String(productName || "").toLowerCase();
  return (
    txt.includes("beanie") ||
    txt.includes("knit cap") ||
    txt.includes("watch cap") ||
    txt.includes("knit hat") ||
    txt.includes("skull cap")
  );
}

// Redondeo comercial hacia ARRIBA a .99 (21.42 → 21.99).
function ceil99(value) {
  const v = Math.round(value * 100) / 100;
  let c = Math.floor(v) + 0.99;
  if (c < v - 1e-9) c += 1.0;
  return Math.round(c * 100) / 100;
}

export const HATS_LEATHER_MIN_QTY = 6;
export const HATS_LEATHER_TIERS = [
  [6, 12, 32.99, "6–12"],
  [13, 24, 31.99, "13–24"],
  [25, 48, 30.99, "25–48"],
  [49, 72, 29.99, "49–72"],
  [73, null, 28.99, "73+"],
];

export const HATS_EMBROIDERY_MIN_QTY = 12;
export const HATS_EMBROIDERY_TIERS = [
  [12, 12, 34.99, "12"],
  [13, 24, 33.99, "13–24"],
  [25, 48, 32.99, "25–48"],
  [49, 72, 31.99, "49–72"],
  [73, null, 30.99, "73+"],
];

function findTier(qty, tiers) {
  for (const [lo, hi, price, label] of tiers) {
    if (qty < lo) continue;
    if (hi === null || qty <= hi) return [lo, hi, price, label];
  }
  return null;
}

function resolveHatsVariant(decorationMethod) {
  const n = (decorationMethod || "").toLowerCase();
  if (n.includes("patch")) return "leather";
  return "embroidery";
}

// MIX & MATCH pools (Jun 2026) — espejo exacto de resolve_mix_pool Python.
// La cantidad COMBINADA del pool valida el mínimo y selecciona el tier.
export const MIX_POOL_BY_CATEGORY = {
  "T-Shirts": "TSHIRTS",
  "Polos/Knits": "POLOS",
  "Sweatshirts/Fleece": "HOODIES",
  Outerwear: "JACKETS",
};

// POLOS ECONÓMICOS (Jun 2026) — +$3.00/unidad si costo base GP < $12.00.
export const ECONOMY_POLO_COST_THRESHOLD = 12.0;
export const ECONOMY_POLO_UPLIFT = 3.0;

export const MIX_POOL_MIN = {
  TSHIRTS: T_SHIRTS_MIN_QTY,
  POLOS: APPAREL_MIN_QTY,
  HOODIES: APPAREL_MIN_QTY,
  JACKETS: APPAREL_MIN_QTY,
  CAPS_EMBROIDERY: HATS_EMBROIDERY_MIN_QTY,
  CAPS_LEATHER: HATS_LEATHER_MIN_QTY,
  BEANIES: BEANIES_MIN_QTY,
};

export const MIX_POOL_LABELS = {
  TSHIRTS: "T-Shirts",
  POLOS: "Polos",
  HOODIES: "Hoodies",
  JACKETS: "Jackets",
  CAPS_EMBROIDERY: "Caps · Embroidery",
  CAPS_LEATHER: "Caps · Leather Patch",
  BEANIES: "Beanies",
};

export function resolveMixPool(category, categoriesAll, productName, decorationMethod) {
  const seen = [category, ...(Array.isArray(categoriesAll) ? categoriesAll : [])].filter(Boolean);
  if (seen.includes("Caps")) {
    if (isBeanie(productName)) return "BEANIES";
    return resolveHatsVariant(decorationMethod) === "leather"
      ? "CAPS_LEATHER"
      : "CAPS_EMBROIDERY";
  }
  for (const c of seen) {
    if (MIX_POOL_BY_CATEGORY[c]) return MIX_POOL_BY_CATEGORY[c];
  }
  return null;
}

export const DEFAULT_SALES_TAX_RATE = 0.0975;

// Main API — mirrors calculate_selling_price(...).
// Returns { ok, unit_price, group, tier_label, base_tier_price,
//           applied_uplifts:[{name,amount}], min_qty, reason }
export function calculateSellingPrice({
  group,
  qty,
  baseCost = 0,
  longSleeve = false,
  size = "",
  decorationMethod = "",
  beanie = false,
  combinedQty = null,
  polo = false,
}) {
  const result = {
    ok: false,
    unit_price: 0,
    group,
    tier_label: "",
    base_tier_price: 0,
    applied_uplifts: [],
    min_qty: 0,
    reason: "",
  };
  if (!VALID_GROUPS.includes(group)) {
    result.reason = `unknown_group:${group}`;
    return result;
  }
  const q = parseInt(qty, 10) || 0;

  if (group === "T_SHIRTS") {
    result.min_qty = T_SHIRTS_MIN_QTY;
    // MIX & MATCH (Jun 2026) — combinedQty (pool) valida mínimo y tier.
    const tq = parseInt(combinedQty, 10) || q;
    if (tq < T_SHIRTS_MIN_QTY) {
      result.reason = `min_qty_${T_SHIRTS_MIN_QTY}`;
      return result;
    }
    const tier = findTier(tq, T_SHIRTS_TIERS);
    if (!tier) {
      result.reason = "no_tier";
      return result;
    }
    const [, , price, label] = tier;
    result.tier_label = label;
    result.base_tier_price = price;
    const ups = [];
    if (longSleeve) ups.push({ name: "Long Sleeve", amount: 3.0 });
    const [upAmt, upLabel] = sizeUplift(size);
    if (upAmt) ups.push({ name: upLabel, amount: upAmt });
    result.applied_uplifts = ups;
    result.unit_price =
      Math.round((price + ups.reduce((s, u) => s + u.amount, 0)) * 100) / 100;
    result.ok = true;
    return result;
  }

  if (group === "APPAREL") {
    result.min_qty = APPAREL_MIN_QTY;
    // MIX & MATCH (Jun 2026) — combinedQty (pool) valida mínimo y tier.
    const tq = parseInt(combinedQty, 10) || q;
    if (tq < APPAREL_MIN_QTY) {
      result.reason = `min_qty_${APPAREL_MIN_QTY}`;
      return result;
    }
    const tier = findTier(tq, APPAREL_TIER_DISCOUNTS);
    if (!tier) {
      result.reason = "no_tier";
      return result;
    }
    const [, , discount, label] = tier;
    const basePrice = Math.round((parseFloat(baseCost || 0) * 2 + 6) * 100) / 100;
    const tierPrice = Math.round((basePrice - discount) * 100) / 100;
    result.tier_label = label;
    result.base_tier_price = tierPrice;
    const ups = [];
    const [upAmt, upLabel] = sizeUplift(size);
    if (upAmt) ups.push({ name: upLabel, amount: upAmt });
    // POLOS ECONÓMICOS (Jun 2026) — +$3.00/unidad en TODOS los tiers si el
    // costo base GP del polo es estrictamente < $12.00. Solo polo=true (pool
    // POLOS); nunca Hoodies/Jackets/etc.
    if (polo && parseFloat(baseCost || 0) < ECONOMY_POLO_COST_THRESHOLD) {
      ups.push({ name: "Economy Polo", amount: ECONOMY_POLO_UPLIFT });
    }
    result.applied_uplifts = ups;
    result.unit_price =
      Math.round((tierPrice + ups.reduce((s, u) => s + u.amount, 0)) * 100) / 100;
    result.ok = true;
    return result;
  }

  if (group === "HATS") {
    // BEANIES (Jun 2026) — tier por cantidad combinada, costo propio por SKU.
    if (beanie) {
      result.min_qty = BEANIES_MIN_QTY;
      const tq = parseInt(combinedQty, 10) || q;
      if (tq < BEANIES_MIN_QTY) {
        result.reason = `min_qty_${BEANIES_MIN_QTY}`;
        return result;
      }
      const bt = BEANIES_TIERS.find(
        ([lo, hi]) => tq >= lo && (hi === null || tq <= hi)
      );
      if (!bt) {
        result.reason = "no_tier";
        return result;
      }
      const [, , mult, floor, label] = bt;
      const calc = (parseFloat(baseCost) || 0) * mult;
      const price = floor >= calc ? floor : ceil99(calc);
      result.tier_label = `${label} (Beanies)`;
      result.base_tier_price = price;
      result.applied_uplifts = [];
      result.unit_price = price;
      result.ok = true;
      return result;
    }
    const variant = resolveHatsVariant(decorationMethod);
    const tiers = variant === "leather" ? HATS_LEATHER_TIERS : HATS_EMBROIDERY_TIERS;
    result.min_qty = variant === "leather" ? HATS_LEATHER_MIN_QTY : HATS_EMBROIDERY_MIN_QTY;
    // MIX & MATCH (Jun 2026) — combinedQty del pool de la MISMA variante
    // (Embroidery y Leather Patch nunca se mezclan; el caller lo garantiza).
    const tqh = parseInt(combinedQty, 10) || q;
    if (tqh < result.min_qty) {
      result.reason = `min_qty_${result.min_qty}`;
      return result;
    }
    const tier = findTier(tqh, tiers);
    if (!tier) {
      result.reason = "no_tier";
      return result;
    }
    const [, , price, label] = tier;
    result.tier_label = `${label} (${variant === "leather" ? "Leather Patch" : "Embroidery"})`;
    result.base_tier_price = price;
    result.applied_uplifts = [];
    result.unit_price = price;
    result.ok = true;
    return result;
  }

  return result;
}

// Order-level rollup — mirrors compute_order_commercial_totals.
export function computeOrderCommercialTotals({
  subtotal,
  discountType = "amount",
  discountValue = 0,
  taxExempt = false,
  salesTaxRate = DEFAULT_SALES_TAX_RATE,
  depositReceived = 0,
}) {
  const st = parseFloat(subtotal || 0);
  const dv = parseFloat(discountValue || 0);
  let disc = discountType === "percent" ? st * (dv / 100) : dv;
  disc = Math.max(0, Math.min(disc, st));
  const taxable = st - disc;
  const rate = parseFloat(salesTaxRate || 0);
  const tax = taxExempt ? 0 : Math.round(taxable * rate * 100) / 100;
  const total = Math.round((taxable + tax) * 100) / 100;
  const dep = parseFloat(depositReceived || 0);
  const balance = Math.round(Math.max(total - dep, 0) * 100) / 100;
  return {
    discount_amount: Math.round(disc * 100) / 100,
    taxable_amount: Math.round(taxable * 100) / 100,
    sales_tax_amount: tax,
    total_amount: total,
    balance_due: balance,
  };
}
